import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { toTextFractionLatex } from "../../../../utils/fractionLatex";

/* ========= Utils ========= */
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  for (let d = 1; d <= 20; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\tfrac{${num}}{${den}}`
        : `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}
const formatFractionLatex = formatMixedFractionLatex;

/* ========= Generator (Δ > 0) ========= */
export function generateTask() {
  const randi = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  let a, b, c, delta, sqrtDelta, x1, x2;

  while (true) {
    // a dodatnie, żeby było zawsze x1 < x2 przy:
    // x1 = (-b - sqrtΔ)/(2a), x2 = (-b + sqrtΔ)/(2a)
    a = randi(2, 5);

    b = randi(-10, 10);
    c = randi(-10, 10);

    // b i c nie mogą być zerem — te przypadki są w innych zadaniach
    if (b === 0 || c === 0) continue;

    delta = b * b - 4 * a * c;
    if (delta <= 0) continue;

    sqrtDelta = Math.trunc(Math.sqrt(delta));
    if (sqrtDelta * sqrtDelta !== delta) continue;

    const denom = 2 * a;

    // standard szkolny: x1 mniejsze, x2 większe
    x1 = (-b - sqrtDelta) / denom;
    x2 = (-b + sqrtDelta) / denom;

    if (Math.abs(x1) <= 20 && Math.abs(x2) <= 20) break;
  }

  const ax = `${a}x^2`;
  const bx = b > 0 ? `+ ${b}x` : `- ${Math.abs(b)}x`;
  const cx = c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`;
  const equationLatex = `${ax} ${bx} ${cx} = 0`.replace(/\s+/g, " ").trim();

  return {
    type: "SolveQuadraticTwoSolutionsTask",
    data: { a, b, c, delta, sqrtDelta, x1, x2, equationLatex }
  };
}

/* ========= Treść ========= */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż równanie: <InlineMath math={data.equationLatex} />
    </p>
  );
}

/* ========= Input ========= */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_1 ='} /></span>
        <FractionInput
          value={userInput?.x1 || ""}
          onChange={(val) => onChange({ ...userInput, x1: val })}
          disabled={disabled}
          label="x₁"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_2 ='} /></span>
        <FractionInput
          value={userInput?.x2 || ""}
          onChange={(val) => onChange({ ...userInput, x2: val })}
          disabled={disabled}
          label="x₂"
        />
      </div>
    </div>
  );
}

/* ========= Checking ========= */
export function checkAnswer({ data }, userInput) {
  const p1 = userInput?.x1?.parsed;
  const p2 = userInput?.x2?.parsed;
  if ([p1, p2].some(v => v == null || isNaN(v))) return { correct: false };

  const { x1, x2 } = data;
  const ok =
    (Math.abs(p1 - x1) < 0.01 && Math.abs(p2 - x2) < 0.01) ||
    (Math.abs(p1 - x2) < 0.01 && Math.abs(p2 - x1) < 0.01);

  return { correct: ok, expected: [x1, x2] };
}

/* ========= Odpowiedź ========= */
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", marginTop: "0.5rem" }}>
      <div><InlineMath math={`x_1 = ${toTextFractionLatex(formatFractionLatex(data.x1))}`} /></div>
      <div style={{ marginTop: "0.5rem" }}><InlineMath math={`x_2 = ${toTextFractionLatex(formatFractionLatex(data.x2))}`} /></div>
    </div>
  );
}

/* ========= Wyjaśnienie ========= */
export function renderExplanation({ data }) {
  const { a, b, c, delta, sqrtDelta, x1, x2 } = data;

  const step1 = String.raw`a = ${a},\quad b = ${b},\quad c = ${c}`;
  const step2a = String.raw`\Delta = b^2 - 4ac`;
  const step2b = String.raw`\Delta = (${b})^2 - 4 \cdot ${a} \cdot (${c}) = ${delta}`;
  const step2c = String.raw`\sqrt{\Delta} = \sqrt{${delta}} = ${sqrtDelta}`;

  const minusBLatex = b < 0 ? `-(${b})` : `-${b}`;
  const denom = 2 * a;

  const x1Lines = String.raw`
\begin{aligned}
x_1 &= \dfrac{-b - \sqrt{\Delta}}{2a} \\[6pt]
x_1 &= \dfrac{${minusBLatex} - ${sqrtDelta}}{2 \cdot ${a}}`
    + (b < 0 ? String.raw` \\[6pt]
x_1 &= \dfrac{${Math.abs(b)} - ${sqrtDelta}}{2 \cdot ${a}}` : "")
    + String.raw` \\[6pt]
x_1 &= \dfrac{${-b - sqrtDelta}}{${denom}} \\[6pt]
x_1 &= ${formatFractionLatex(x1)}
\end{aligned}
`;

  const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{-b + \sqrt{\Delta}}{2a} \\[6pt]
x_2 &= \dfrac{${minusBLatex} + ${sqrtDelta}}{2 \cdot ${a}}`
    + (b < 0 ? String.raw` \\[6pt]
x_2 &= \dfrac{${Math.abs(b)} + ${sqrtDelta}}{2 \cdot ${a}}` : "")
    + String.raw` \\[6pt]
x_2 &= \dfrac{${-b + sqrtDelta}}{${denom}} \\[6pt]
x_2 &= ${formatFractionLatex(x2)}
\end{aligned}
`;

  return (
    <div className="space-y-4">
      <p><strong>Krok 1:</strong> Współczynniki:</p>
      <BlockMath math={step1} />

      <p><strong>Krok 2:</strong> Liczymy deltę:</p>
      <BlockMath math={step2a} />
      <BlockMath math={step2b} />
      <BlockMath math={step2c} />

      <p><strong>Krok 3:</strong> Obliczamy pierwiastki:</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_1'} />
          </div>
          <BlockMath math={x1Lines} />
        </div>

        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={'x_2'} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={String.raw`x_1 = ${formatFractionLatex(x1)},\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}

/* ========= User answer preview ========= */
export function renderUserAnswer(userInput) {
  if (!userInput?.x1?.raw || !userInput?.x2?.raw) return <i>(brak odpowiedzi)</i>;

  const x1 = formatLatexPreview(userInput.x1.raw, "x_1");
  const x2 = formatLatexPreview(userInput.x2.raw, "x_2");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x1} /></div>
      <div style={{ marginTop: "0.5rem" }}><InlineMath math={x2} /></div>
    </div>
  );
}
